import React from 'react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { CheckCircle, Clock, Users, ShoppingBag, Calendar, MapPin, ArrowRight, Star, Shield } from 'lucide-react';

const RetailAttendanceSoftware = () => {
  const handleGetStarted = () => {
    window.location.href = '/manager-dashboard';
  };

  const handleProTrial = () => {
    window.open('https://buy.stripe.com/5kQ9ATgrw2EWb1nci42ZO0a', '_blank');
  };

  const retailChallenges = [
    {
      challenge: "Multiple Store Locations",
      solution: "Track staff across all your retail locations from one central dashboard"
    },
    {
      challenge: "Shift Pattern Complexity",
      solution: "Handle early opens, late closes, and weekend shifts with flexible scheduling"
    },
    {
      challenge: "High Staff Turnover",
      solution: "Quick employee onboarding with simple PIN-based access"
    },
    {
      challenge: "Seasonal Staff Management",
      solution: "Easily add temporary staff during busy periods like Christmas and sales"
    }
  ];

  const retailFeatures = [
    {
      icon: <ShoppingBag className="w-6 h-6" />,
      title: "Multi-Store Management",
      description: "Manage staff attendance across multiple retail locations from one dashboard. Perfect for fashion chains, convenience stores, and retail franchises."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Flexible Shift Tracking",
      description: "Handle complex retail schedules including split shifts, break coverage, and variable hours. Track full-time, part-time, and seasonal workers."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Easy Staff Onboarding",
      description: "Add new retail staff in seconds. Simple PIN-based system means no training required - perfect for high-turnover retail environments."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Peak Period Management",
      description: "Handle busy shopping periods, sales events, and seasonal rushes with accurate time tracking for all your retail staff."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src="/tyme-logo-new.png" alt="Tyme Retail Attendance Software" className="h-12" />
          </a>
          <Button onClick={handleGetStarted} className="bg-gradient-to-r from-indigo-600 to-purple-600">
            Start Free Trial
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Retail <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Employee Attendance Software</span> UK
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              Purpose-built staff attendance management for UK retail businesses. Handle multiple stores, complex shift patterns, and seasonal staff with our comprehensive retail workforce management solution.
            </p>

            {/* Retail Stats */}
            <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-indigo-100">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">Multi-Store</div>
                  <div className="text-sm text-slate-600">Manage all locations</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">Real-Time</div>
                  <div className="text-sm text-slate-600">Live attendance tracking</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">Retail-Focused</div>
                  <div className="text-sm text-slate-600">Built for retail needs</div>
                </div>
              </div>
            </div>

            <Button 
              onClick={handleProTrial}
              size="lg"
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-8 py-4 text-lg font-semibold"
            >
              Start 3-Day Pro Trial - Perfect for Retail
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Retail Challenges Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-12">
            Retail Attendance Challenges? We've Got You Covered
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {retailChallenges.map((item, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-red-600 font-bold">?</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900 mb-2">{item.challenge}</h3>
                  <p className="text-slate-600">{item.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-slate-900 mb-12">
            Retail-Specific Employee Attendance Features
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {retailFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center text-indigo-600 mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Retail Success Story */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                How Fashion Forward Cut Payroll Processing Time by 75%
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">The Challenge</h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Fashion Forward, a 5-store clothing chain in Manchester, was spending 8+ hours per week manually calculating timesheets for 40+ retail staff across different shift patterns and locations.
                  </p>
                  
                  <h3 className="font-semibold text-slate-800 mb-3">The Solution</h3>
                  <p className="text-slate-600 text-sm">
                    Implemented Tyme's retail attendance software across all 5 locations with PIN-based clock in/out and automated timesheet generation.
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3">The Results</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-slate-600">75% reduction in payroll processing time</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-slate-600">£200+ monthly savings on administrative costs</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-slate-600">100% accurate timesheets across all stores</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-slate-600">Real-time visibility into staff attendance</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Retail Types */}
      <section className="py-16 px-4 bg-gradient-to-r from-slate-100 to-slate-200">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Built for UK Retail Businesses
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Fashion & Clothing Stores</h3>
              <p className="text-slate-600 text-sm">High street fashion, boutiques, and clothing chains</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Convenience Stores</h3>
              <p className="text-slate-600 text-sm">Local shops, newsagents, and convenience chains</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Specialty Retail</h3>
              <p className="text-slate-600 text-sm">Electronics, home goods, and specialty retailers</p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Solutions Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center text-slate-900 mb-12">
            Explore More Attendance Solutions
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Small Business Solutions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">
                  If you're a smaller retailer or starting out, explore our tailored solutions for small businesses with simplified features.
                </p>
                <a 
                  href="/small-business-time-clock" 
                  className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2 group"
                >
                  View Small Business Features
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-indigo-600" />
                  Enterprise Comparison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm mb-3">
                  Large retail chains: see how Tyme compares to enterprise solutions like Kronos UK and Mitrefinch.
                </p>
                <a 
                  href="/time-tracking-software" 
                  className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2 group"
                >
                  Compare Enterprise Solutions
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <p className="text-slate-600 mb-4">
              Want to explore all our workforce management features?
            </p>
            <a 
              href="/" 
              className="text-indigo-600 hover:text-indigo-800 font-medium inline-flex items-center gap-2"
            >
              ← Discover Complete Feature Set
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Retail Staff Management?
          </h2>
          <p className="text-xl mb-8 text-indigo-100 max-w-2xl mx-auto">
            Join UK retail businesses using Tyme to streamline staff attendance, 
            improve payroll accuracy, and focus on what matters most - serving customers. Try Pro free for 3 days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handleProTrial}
              size="lg"
              className="bg-white text-indigo-600 hover:bg-slate-50 px-8 py-4 text-lg font-semibold"
            >
              Start 3-Day Pro Trial Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          
          <div className="mt-8 text-indigo-200 text-sm">
            ✓ 3-day Pro trial ✓ Multi-store support ✓ No setup fees ✓ UK retail expertise
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 px-4">
        <div className="container mx-auto text-center">
          <img src="/tyme-logo-new.png" alt="Tyme" className="h-12 mx-auto mb-6" />
          <p className="text-slate-400 mb-4">Retail employee attendance software built for UK businesses</p>
          <p className="text-slate-500 text-sm">
            Multi-store management • Retail shift tracking • Staff attendance solutions
          </p>
        </div>
      </footer>
    </div>
  );
};

export default RetailAttendanceSoftware;